<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import * as yup from 'yup';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import timezone from 'dayjs/plugin/timezone';

  import { Checkbox, ComboBox, FormGroup, InlineNotification, Modal, NumberInput, TimePicker } from 'carbon-components-svelte';

  import { Form } from '@mst-fe/sveltejs-forms';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';

  import { DataInventoryServiceIdToName } from '../../../../shared/constants';
  import { getServerErrorMessage } from '../../../utils';
  import { getDataFeedMetadata, updateMetadata } from '../../../services';

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const dispatch = createEventDispatcher();
  const timezones = Intl.supportedValuesOf('timeZone').map((tz) => ({ id: tz, text: tz, timezone: dayjs().tz(tz).format('Z') }));
  const schema = yup
    .object()
    .shape({
      tradingStartTime: yup
        .string()
        .nullable()
        .transform((v, ov) => (ov === '' ? null : v))
        .test('valid-time', 'Enter a valid time in HH:mm (00:00-23:59)', (v) => v == null || /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(v)),
      timezone: yup
        .string()
        .nullable()
        .test('valid-timezone', 'Select a valid timezone', (v) => v == null || timezones.some((tz) => tz.id === v)),
      defaultPermissionDelayMinutes: yup.number().when(['tradingStartTime', 'timezone'], {
        is: (tradingStartTime, timezone) => !!tradingStartTime && !!timezone,
        then: (s) =>
          s
            .typeError('Minutes must be a number')
            .min(0, 'Minutes must be greater than or equal to 0')
            .max(59, 'Minutes must be less than or equal to 59'),
        otherwise: (s) =>
          s
            .transform((value, originalValue) => (originalValue === '' || originalValue == null ? 0 : value))
            .default(0)
            .oneOf([0], 'Minutes must be 0 unless Trading End Time and Timezone are set'),
      }),
      defaultPermissionDelayHours: yup.number().when(['tradingStartTime', 'timezone'], {
        is: (tradingStartTime, timezone) => !!tradingStartTime && !!timezone,
        then: (s) =>
          s
            .typeError('Hours must be a number')
            .min(0, 'Hours must be greater than or equal to 0')
            .max(23, 'Hours must be less than or equal to 23'),
        otherwise: (s) =>
          s
            .transform((value, originalValue) => (originalValue === '' || originalValue == null ? 0 : value))
            .default(0)
            .oneOf([0], 'Hours must be 0 unless Trading End Time and Timezone are set'),
      }),
      defaultPermissionDelayDays: yup.number().when(['tradingStartTime', 'timezone'], {
        is: (tradingStartTime, timezone) => !!tradingStartTime && !!timezone,
        then: (s) => s.typeError('Days must be a number').min(0, 'Days must be greater than or equal to 0'),
        otherwise: (s) =>
          s
            .transform((value, originalValue) => (originalValue === '' || originalValue == null ? 0 : value))
            .default(0)
            .oneOf([0], 'Days must be 0 unless Trading End Time and Timezone are set'),
      }),
      defaultPermissionDelayMonths: yup.number().when(['tradingStartTime', 'timezone'], {
        is: (tradingStartTime, timezone) => !!tradingStartTime && !!timezone,
        then: (s) => s.typeError('Months must be a number').min(0, 'Months must be greater than or equal to 0'),
        otherwise: (s) =>
          s
            .transform((value, originalValue) => (originalValue === '' || originalValue == null ? 0 : value))
            .default(0)
            .oneOf([0], 'Months must be 0 unless Trading End Time and Timezone are set'),
      }),
      defaultPermissionCalendarMonthDelay: yup.boolean().default(false),
    })
    .test('paired-timezone-starttime', 'Timezone and Trading End Time must both be set or both be empty', function (values) {
      const { tradingStartTime, timezone } = values;

      if (!!timezone && !tradingStartTime) {
        return this.createError({
          path: 'tradingStartTime',
          message: 'Trading End Time is required when Timezone is set',
        });
      }

      if (!!tradingStartTime && !timezone) {
        return this.createError({
          path: 'timezone',
          message: 'Timezone is required when Trading End Time is set',
        });
      }

      return true;
    });

  export let open = false,
    dataFeedId,
    dataFeedName,
    serviceId;

  let resultNotification,
    formSubmitButtonRef,
    metadata = null,
    loading = false;

  async function fetchSavedSettings() {
    loading = true;
    try {
      const [savedMetadata] = await getDataFeedMetadata(serviceId, dataFeedId);
      metadata = {
        tradingStartTime: savedMetadata?.tradingStartTime ? savedMetadata.tradingStartTime.substring(0, 5) : null,
        timezone: savedMetadata?.timezone ?? null,
        defaultPermissionDelayMonths: savedMetadata?.defaultPermissionDelayMonths ?? 0,
        defaultPermissionDelayDays: savedMetadata?.defaultPermissionDelayDays ?? 0,
        defaultPermissionDelayHours: savedMetadata?.defaultPermissionDelayHours ?? 0,
        defaultPermissionDelayMinutes: savedMetadata?.defaultPermissionDelayMinutes ?? 0,
        defaultPermissionCalendarMonthDelay: savedMetadata?.defaultPermissionCalendarMonthDelay ?? false,
      };
    } catch (error) {
      console.error('[DataFeedSettingsPerInventoryModal] Failed to fetch saved settings!', error);
    } finally {
      loading = false;
    }
  }

  async function onFormSubmit({ detail: { values } }) {
    resultNotification = null;
    loading = true;

    try {
      await schema.validate(values, { abortEarly: false });
      await updateMetadata(dataFeedId, {
        ...values,
        serviceId,
      });
      dispatch('close');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.details?.[0]?.context?.message ??
        getServerErrorMessage(error) ??
        'Verify your submission and try again!';
      console.error('[ApprovalModal] Failed to update metadata!', errorMessage);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to update metadata! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function shouldFilterItem(item, value) {
    if (!value) {
      return true;
    }

    return item.text.toLowerCase().includes(value.toLowerCase());
  }

  function handleCustomChangeEvent({ key, setValue }) {
    return function onCustomChange({ detail: value }) {
      if (key === 'defaultPermissionDelayMonths' && !value) {
        setValue('defaultPermissionCalendarMonthDelay', false);
      }

      setValue(key, value);
    };
  }

  function handleChangeEvent({ key, setValue }) {
    return function onChange({ target: { value } }) {
      setValue(key, value);
    };
  }

  function handleSelectEvent({ key, setValue }) {
    return function onSelect({ detail: { selectedId } }) {
      setValue(key, selectedId);
    };
  }

  function handleClose() {
    if (loading) {
      return;
    }

    dispatch('close');
  }

  function onModalSubmit() {
    if (!formSubmitButtonRef) {
      throw new Error('Form submit button not found!');
    }

    formSubmitButtonRef.click();
  }

  function valuesToZero(values) {
    return (
      values.defaultPermissionDelayDays === 0 &&
      values.defaultPermissionDelayHours === 0 &&
      values.defaultPermissionDelayMinutes === 0 &&
      values.defaultPermissionDelayMonths === 0 &&
      (metadata?.defaultPermissionDelayDays ||
        metadata?.defaultPermissionDelayHours ||
        metadata?.defaultPermissionDelayMinutes ||
        metadata?.defaultPermissionDelayMonths)
    );
  }

  onMount(fetchSavedSettings);
</script>

<Modal
  bind:open
  modalHeading={`${DataInventoryServiceIdToName[serviceId]} Settings`}
  modalLabel={dataFeedName}
  primaryButtonText="Update"
  secondaryButtonText="Cancel"
  shouldSubmitOnEnter={false}
  preventCloseOnClickOutside
  primaryButtonDisabled={loading}
  on:click:button--secondary={handleClose}
  on:close={handleClose}
  on:submit={onModalSubmit}
>
  <LoadingSpinner {loading} small>
    {#if resultNotification}
      <InlineNotification
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
        hideCloseButton
      />
    {/if}
    <p class="main">
      Changes below will affect any entitlements the next time the AWS Data Feed Reconciliation (All Groups) task is triggered.
    </p>
    {#if metadata}
      <Form
        fieldDomString="input,select,textarea,div[name]"
        {schema}
        initialValues={metadata}
        validateOnBlur={true}
        validateOnChange={true}
        on:submit={onFormSubmit}
        let:submitForm
        let:setValue
        let:errors
        let:touched
        let:values
      >
        <FormGroup legendText="Trading End Time">
          <div class="time-select-wrapper">
            <TimePicker
              placeholder="hh:mm"
              invalid={touched.tradingStartTime && !!errors.tradingStartTime}
              invalidText={errors.tradingStartTime}
              on:change={handleChangeEvent({ key: 'tradingStartTime', setValue })}
              name="tradingStartTime"
              value={values.tradingStartTime}
            />
            <ComboBox
              {shouldFilterItem}
              items={timezones}
              name="timezone"
              placeholder="Time zone"
              value={values.timezone}
              selectedId={values.timezone}
              invalid={touched.timezone && !!errors.timezone}
              invalidText={errors.timezone}
              on:clear={() => setValue('timezone', null)}
              on:select={handleSelectEvent({ key: 'timezone', setValue })}
              let:item
            >
              <div>
                <strong>{item.text}</strong>
              </div>
              <div>UTC{item.timezone}</div>
            </ComboBox>
          </div>
          <div class="bx--form__helper-text">Format: HH:mm (00:00-23:59)</div>
        </FormGroup>
        {#if valuesToZero(values)}
          <InlineNotification
            kind="warning"
            lowContrast
            subtitle="Setting all delay values to zero will remove any overwritten permission delays for this data feed across all groups."
            hideCloseButton
          />
        {/if}
        <FormGroup legendText="Default Permission Delay">
          <div class="number-inputs">
            <div class="month-delay-wrapper">
              <NumberInput
                labelText="Months"
                name="defaultPermissionDelayMonths"
                size="sm"
                min={0}
                placeholder="0"
                value={values.defaultPermissionDelayMonths}
                invalid={touched.defaultPermissionDelayMonths && !!errors.defaultPermissionDelayMonths}
                invalidText={errors.defaultPermissionDelayMonths}
                on:change={handleCustomChangeEvent({ key: 'defaultPermissionDelayMonths', setValue })}
                disableWheel
              />
              <Checkbox
                name="defaultPermissionCalendarMonthDelay"
                labelText="Calendar Month Delay"
                disabled={!values.defaultPermissionDelayMonths}
                checked={values.defaultPermissionCalendarMonthDelay}
                on:check={handleCustomChangeEvent({ key: 'defaultPermissionCalendarMonthDelay', setValue })}
              />
            </div>
            <NumberInput
              labelText="Days"
              name="defaultPermissionDelayDays"
              size="sm"
              min={0}
              placeholder="0"
              value={values.defaultPermissionDelayDays}
              invalid={touched.defaultPermissionDelayDays && !!errors.defaultPermissionDelayDays}
              invalidText={errors.defaultPermissionDelayDays}
              on:change={handleCustomChangeEvent({ key: 'defaultPermissionDelayDays', setValue })}
              disableWheel
            />
            <NumberInput
              labelText="Hours"
              name="defaultPermissionDelayHours"
              size="sm"
              min={0}
              max={23}
              placeholder="0"
              value={values.defaultPermissionDelayHours}
              invalid={touched.defaultPermissionDelayHours && !!errors.defaultPermissionDelayHours}
              invalidText={errors.defaultPermissionDelayHours}
              on:change={handleCustomChangeEvent({ key: 'defaultPermissionDelayHours', setValue })}
              disableWheel
            />
            <NumberInput
              labelText="Minutes"
              name="defaultPermissionDelayMinutes"
              size="sm"
              min={0}
              max={59}
              placeholder="0"
              value={values.defaultPermissionDelayMinutes}
              invalid={touched.defaultPermissionDelayMinutes && !!errors.defaultPermissionDelayMinutes}
              invalidText={errors.defaultPermissionDelayMinutes}
              on:change={handleCustomChangeEvent({ key: 'defaultPermissionDelayMinutes', setValue })}
              disableWheel
            />
          </div>
        </FormGroup>
        <button hidden type="button" on:click={submitForm} bind:this={formSubmitButtonRef} />
      </Form>
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  .main {
    margin-bottom: 1rem;
  }

  .time-select-wrapper {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .time-select-wrapper :global(.bx--form-item) {
    flex-grow: 0;
    margin-right: 0.5rem;
  }

  .time-select-wrapper :global(.bx--time-picker .bx--select-input) {
    width: inherit;
  }

  .number-inputs :global(.bx--form-item) {
    display: flex;
    flex-direction: column;
  }

  .number-inputs :global(.bx--label) {
    margin-bottom: 0.25rem;
    margin-top: 0.5rem;
  }

  .month-delay-wrapper {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .month-delay-wrapper :global(.bx--checkbox-label) {
    margin-left: 0.5rem;
  }

  .month-delay-wrapper :global(.bx--checkbox-label-text) {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
  }
</style>
